import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EditIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import FolderIcon from "@mui/icons-material/Folder";
import ImageIcon from "@mui/icons-material/Image";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MapIcon from "@mui/icons-material/Map";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import { alpha } from "@mui/material/styles";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

const limitOptions = [10, 25, 50, 100, 200];
const tableHeadBg = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100];
const translucentPaperBg = (theme) =>
  theme.palette.mode === "dark"
    ? alpha(theme.palette.background.paper, 0.9)
    : alpha(theme.palette.background.paper, 0.96);

const emptyUnitForm = {
  nombre: "",
  uid: "",
  hwTypeId: "",
  sensorAlias: "",
  chipNumero: "",
  iconLibId: "",
  iconPath: "",
  grupoIds: [],
  observacion: "",
};

const getUnidadId = (u) => u?.id ?? u?.sys_id ?? u?.sysId;

const getUnidadNombre = (u) => u?.nm || u?.name || "-";

const getUnidadUid = (u) => u?.uid || u?.uid2 || "-";

const getUnidadGrupos = (u) => Array.isArray(u?.grupos) ? u.grupos : [];

const getIconValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    return String(value.url || value.href || value.uri || value.path || value.n || value.name || "").trim();
  }
  return "";
};

const getUnidadIcon = (u) => {
  const prp = u?.prp || u?.properties || {};
  const candidates = [
    u?.icono_url,
    u?.iconUrl,
    u?.imageUrl,
    u?.uri,
    u?.uimg,
    u?.img,
    u?.icon,
    u?.image,
    prp?.icon,
    prp?.unit_icon,
    prp?.image,
    prp?.uri,
  ];

  const raw = candidates.map(getIconValue).find(Boolean) || "";
  if (!raw) return { url: "", path: "" };

  if (raw.startsWith("/avl_library_image/") || raw.startsWith("avl_library_image/")) {
    const path = normalizarPathIconoUnidad(raw);
    return { url: "", path };
  }

  const path = raw.startsWith("library/") ? raw : raw.includes("/") ? raw : `library/unit/${raw}`;
  const url = /^https?:\/\//i.test(raw) ? raw : "";
  return { url, path };
};

const normalizeIconKey = (value) => String(value || "").trim().toLowerCase();

const normalizarPathIconoUnidad = (valor) => {
  const raw = String(valor || "").trim();
  if (!raw) return "";
  const withoutQuery = raw.split("?")[0];
  const match = withoutQuery.match(/avl_library_image\/\d+\/\d+\/(.+)$/i);
  if (match?.[1]) return decodeURIComponent(match[1]);
  return withoutQuery;
};

const getIconFileName = (value) => String(value || "").split("/").filter(Boolean).pop() || "";

const buildIconUrlMap = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    const url = item?.imageUrl || item?.url || "";
    if (!url) return;

    const keys = [
      item?.path,
      item?.fullPath,
      item?.n,
      item?.name,
      getIconFileName(item?.path || item?.fullPath || item?.n || item?.name),
    ];

    keys.map(normalizeIconKey).filter(Boolean).forEach((key) => map.set(key, url));
  });
  return map;
};

const resolveUnidadIconUrl = (icon, iconUrlMap) => {
  if (icon?.url) return icon.url;
  const keys = [
    icon?.path,
    getIconFileName(icon?.path),
  ];
  return keys.map(normalizeIconKey).map((key) => iconUrlMap.get(key)).find(Boolean) || "";
};

const getUnidadSensorAlias = (u) => {
  const sensores = u?.sens || u?.sensors || {};
  const found = Object.values(sensores).find((sensor) =>
    String(sensor?.n || sensor?.name || "").toUpperCase().startsWith("ALIAS:")
  );
  return found?.n || found?.name || "";
};

const getGrupoId = (grupo) => grupo?.id ?? grupo?.itemId ?? grupo?.sys_id;
const getGrupoNombre = (grupo) => grupo?.nm || grupo?.name || grupo?.nombre || "-";

const mergeById = (base = [], extra = [], getId) => {
  const map = new Map();
  [...base, ...extra].forEach((item) => {
    const id = getId(item);
    if (id !== undefined && id !== null && String(id).trim() !== "") {
      map.set(String(id), item);
    }
  });
  return [...map.values()];
};

const toggleId = (ids = [], id) => {
  const value = String(id);
  const next = new Set(ids.map((item) => String(item)));
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return [...next];
};

function GroupCheckList({
  rows = [],
  selectedIds = [],
  onChange,
  loading = false,
  filter = "",
  onFilterChange,
  page = 1,
  totalPages = 1,
  onPageChange,
}) {
  const selectedSet = useMemo(() => new Set(selectedIds.map((id) => String(id))), [selectedIds]);
  const visibleIds = rows.map(getGrupoId).filter((id) => id !== undefined && id !== null);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(String(id)));

  const toggleVisible = () => {
    const next = new Set(selectedSet);
    if (allVisibleSelected) {
      visibleIds.forEach((id) => next.delete(String(id)));
    } else {
      visibleIds.forEach((id) => next.add(String(id)));
    }
    onChange?.([...next]);
  };

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", gridColumn: { xs: "auto", md: "1 / span 2" } }}>
      <Box sx={{ p: 0.75, display: "flex", alignItems: "center", gap: 0.75, borderBottom: "1px solid #e5e7eb" }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 900, flex: 1 }}>
          Grupos
        </Typography>
        <Chip size="small" label={`${selectedSet.size} seleccionado(s)`} sx={{ height: 20, fontSize: "0.68rem" }} />
      </Box>
      <Box sx={{ p: 0.75, borderBottom: "1px solid #e5e7eb" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar grupo..."
          value={filter}
          onChange={(event) => onFilterChange?.(event.target.value)}
          InputProps={{
            endAdornment: loading ? <CircularProgress color="inherit" size={16} /> : null,
          }}
          sx={{ "& .MuiInputBase-input": { py: 0.55, fontSize: "0.76rem" } }}
        />
      </Box>
      <TableContainer sx={{ maxHeight: 240 }}>
        <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.35, px: 0.6, fontSize: "0.72rem" } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ bgcolor: tableHeadBg }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && visibleIds.some((id) => selectedSet.has(String(id)))}
                  onChange={toggleVisible}
                />
              </TableCell>
              <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Grupo</TableCell>
              <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 2.5 }}>
                  <CircularProgress size={18} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  Sin grupos para mostrar.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((grupo) => {
                const id = getGrupoId(grupo);
                const checked = selectedSet.has(String(id));
                return (
                  <TableRow key={String(id)} hover onClick={() => onChange?.(toggleId(selectedIds, id))} sx={{ cursor: "pointer" }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={checked} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: checked ? 900 : 600 }}>{getGrupoNombre(grupo)}</TableCell>
                    <TableCell>{id || "-"}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {onPageChange && totalPages > 1 && (
        <Box sx={{ px: 0.75, py: 0.6, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.75, borderTop: "1px solid #e5e7eb" }}>
          <Button size="small" disabled={loading || page <= 1} onClick={() => onPageChange(page - 1)} sx={{ minWidth: 64, fontSize: "0.7rem", py: 0.25 }}>
            Anterior
          </Button>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
            {page} / {totalPages}
          </Typography>
          <Button size="small" disabled={loading || page >= totalPages} onClick={() => onPageChange(page + 1)} sx={{ minWidth: 64, fontSize: "0.7rem", py: 0.25 }}>
            Siguiente
          </Button>
        </Box>
      )}
    </Paper>
  );
}

const getUnidadHwType = (u) => {
  const raw = u?.hwType || u?.hw_type || u?.hw;
  if (raw && typeof raw === "object") {
    const id = raw.id ?? raw.hw_id ?? raw.hwTypeId;
    if (!id) return null;
    return {
      ...raw,
      id,
      name: raw.name || raw.nombre || raw.nm || `ID ${id}`,
    };
  }

  const id =
    u?.hwTypeId ??
    u?.hw_type_id ??
    u?.hw_id ??
    u?.hw ??
    u?.deviceTypeId ??
    u?.device_type_id ??
    u?.tipo_dispositivo_id;

  if (!id) return null;

  return {
    id,
    name:
      u?.hwTypeName ||
      u?.hw_type_name ||
      u?.hw_name ||
      u?.hwName ||
      u?.deviceTypeName ||
      u?.device_type ||
      u?.tipo_dispositivo ||
      `ID ${id}`,
    tp: u?.hw_tp || u?.tp,
    up: u?.hw_up || u?.up,
  };
};

const getHwOptionLabel = (option) =>
  option ? `${option.name || option.nombre || option.nm || "-"} (${option.id || "-"})` : "";

const getChipOptionLabel = (option) =>
  option ? `${option.numero || "-"}${option.emai ? ` / ${option.emai}` : ""}${option.modelo ? ` - ${option.modelo}` : ""}` : "";

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateTime = (value) => {
  if (!value) return "-";
  let date = null;

  if (typeof value === "number") {
    date = new Date(value < 100000000000 ? value * 1000 : value);
  } else {
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && String(value).trim() !== "") {
      date = new Date(numeric < 100000000000 ? numeric * 1000 : numeric);
    } else {
      date = new Date(value);
    }
  }

  if (!date || Number.isNaN(date.getTime())) return String(value);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

const formatTimeShort = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "-";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const formatDateLong = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-BO", { day: "2-digit", month: "long", year: "numeric" });
};

const getParamValue = (obj, key) => {
  const raw = obj?.[key];
  if (raw && typeof raw === "object" && "v" in raw) return raw.v;
  return raw;
};

const getUnidadPosition = (u) => {
  const prms = u?.prms || u?.params || {};
  const pos = u?.pos || u?.lmsg?.pos || u?.message?.pos || {};
  const lat = pos.y ?? pos.lat ?? u?.["pos.y"] ?? getParamValue(prms, "pos.y");
  const lng = pos.x ?? pos.lon ?? pos.lng ?? u?.["pos.x"] ?? getParamValue(prms, "pos.x");

  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return null;
  if (latNum === 0 && lngNum === 0) return null;
  return { lat: latNum, lng: lngNum };
};

const getUnidadLastTime = (u) => {
  const prms = u?.prms || u?.params || {};
  return (
    u?.pos?.t ||
    u?.lmsg?.t ||
    u?.lmsg?.pos?.t ||
    u?.message?.t ||
    u?.tm ||
    u?.last_message_time ||
    getParamValue(prms, "pos.t") ||
    getParamValue(prms, "last_message_time")
  );
};

export default function PlataformaUnidadesPage({
  title,
  subtitle,
  listUnits,
  listHwTypes,
  listUnitGroups,
  listIconLibrary,
  listChips,
  createUnit,
  updateUnit,
  deleteUnit,
  syncUnit,
  providerColor = "#006d75",
}) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [err, setErr] = useState("");
  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [mapDialog, setMapDialog] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [editRow, setEditRow] = useState(null);
  const [hwInput, setHwInput] = useState("");
  const [hwOptions, setHwOptions] = useState([]);
  const [hwLoading, setHwLoading] = useState(false);
  const [hwSelected, setHwSelected] = useState(null);
  const [groupInput, setGroupInput] = useState("");
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupPage, setGroupPage] = useState(1);
  const [groupTotalPages, setGroupTotalPages] = useState(1);
  const [chipInput, setChipInput] = useState("");
  const [chipOptions, setChipOptions] = useState([]);
  const [chipSelected, setChipSelected] = useState(null);
  const [chipLoading, setChipLoading] = useState(false);
  const [iconDialog, setIconDialog] = useState({ open: false, target: "create", path: "" });
  const [iconItems, setIconItems] = useState([]);
  const [iconUrlMap, setIconUrlMap] = useState(() => new Map());
  const [iconLoading, setIconLoading] = useState(false);
  const [iconError, setIconError] = useState("");
  const [createForm, setCreateForm] = useState(emptyUnitForm);
  const [editForm, setEditForm] = useState(emptyUnitForm);

  const fetchRows = async ({ usePage = page, useSearch = appliedSearch, useLimit = limit } = {}) => {
    setErr("");
    setLoading(true);
    try {
      const resp = await listUnits({ search: useSearch, page: usePage, limit: useLimit });
      const payload = resp?.data || {};
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotal(Number(payload.total || 0));
      setTotalPages(Number(payload.totalPages || 1));
      setLastUpdated(new Date());
    } catch (e) {
      setErr(e?.response?.data?.error || e?.response?.data?.message || e.message || "Error listando unidades");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const loadGroups = async ({ usePage = groupPage, useSearch = groupInput } = {}) => {
    if (!listUnitGroups) return;

    setGroupLoading(true);
    try {
      const resp = await listUnitGroups({ search: String(useSearch || "").trim(), page: usePage, limit: 50 });
      const payload = resp?.data || {};
      const data = Array.isArray(payload.data) ? payload.data : [];
      const current = editOpen ? getUnidadGrupos(editRow) : [];
      setGroupOptions(mergeById(current, data, getGrupoId));
      setGroupPage(Number(payload.page || usePage || 1));
      setGroupTotalPages(Number(payload.totalPages || 1));
    } catch (e) {
      setGroupOptions([]);
      setGroupTotalPages(1);
      setSnack({
        open: true,
        type: "error",
        msg: e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo cargar grupos",
      });
    } finally {
      setGroupLoading(false);
    }
  };

  const loadIconLibrary = async ({ target = iconDialog.target } = {}) => {
    if (!listIconLibrary) return;
    setIconLoading(true);
    setIconError("");
    try {
      const resp = await listIconLibrary({ type: "unit", flags: 0, resId: 0, size: 48 });
      const payload = resp?.data?.data || resp?.data || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      setIconItems(items);
      setIconUrlMap(buildIconUrlMap(items));
      setIconDialog({ open: true, target, path: payload.type || "unit" });
    } catch (e) {
      setIconError(e?.response?.data?.message || e?.response?.data?.error || e.message || "No se pudo cargar la biblioteca de iconos");
    } finally {
      setIconLoading(false);
    }
  };

  const openIconLibrary = (target) => {
    setIconDialog({ open: true, target, path: "unit" });
    loadIconLibrary({ target });
  };

  useEffect(() => {
    if (!listIconLibrary) return undefined;

    let active = true;
    (async () => {
      try {
        const resp = await listIconLibrary({ type: "unit", flags: 0, resId: 0, size: 32 });
        const payload = resp?.data?.data || resp?.data || {};
        const items = Array.isArray(payload.items) ? payload.items : [];
        if (active) setIconUrlMap(buildIconUrlMap(items));
      } catch (e) {
        if (active) setIconUrlMap(new Map());
      }
    })();

    return () => {
      active = false;
    };
  }, [listIconLibrary]);

  const selectIcon = (item) => {
    const fieldSetter = iconDialog.target === "edit" ? setEditForm : setCreateForm;
    fieldSetter((prev) => ({
      ...prev,
      iconLibId: "0",
      iconPath: item.path || "",
    }));
    setIconDialog({ open: false, target: iconDialog.target, path: "" });
  };

  useEffect(() => {
    if ((!createOpen && !editOpen) || !listHwTypes) return undefined;

    let active = true;
    const timer = setTimeout(async () => {
      setHwLoading(true);
      try {
        const resp = await listHwTypes({ search: hwInput.trim(), page: 1, limit: 30 });
        const payload = resp?.data || {};
        const options = Array.isArray(payload.data) ? payload.data : [];
        const merged = hwSelected?.id && !options.some((option) => String(option?.id) === String(hwSelected.id))
          ? [hwSelected, ...options]
          : options;
        if (active) setHwOptions(merged);
      } catch (e) {
        if (active) {
          setHwOptions([]);
          setSnack({
            open: true,
            type: "error",
            msg: e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo cargar tipos de dispositivo",
          });
        }
      } finally {
        if (active) setHwLoading(false);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [createOpen, editOpen, hwInput, hwSelected, listHwTypes]);

  useEffect(() => {
    if ((!createOpen && !editOpen) || !listUnitGroups) return undefined;

    const timer = setTimeout(() => {
      setGroupPage(1);
      loadGroups({ usePage: 1, useSearch: groupInput });
    }, 350);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen, editOpen, groupInput, listUnitGroups, editRow]);

  useEffect(() => {
    if (!createOpen || !listChips) return undefined;

    let active = true;
    const timer = setTimeout(async () => {
      setChipLoading(true);
      try {
        const resp = await listChips({ search: chipInput.trim(), limit: 30 });
        const payload = resp?.data || {};
        const options = Array.isArray(payload.data) ? payload.data : [];
        if (active) setChipOptions(options);
      } catch (e) {
        if (active) {
          setChipOptions([]);
          setSnack({
            open: true,
            type: "error",
            msg: e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo cargar chips disponibles",
          });
        }
      } finally {
        if (active) setChipLoading(false);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [createOpen, chipInput, listChips]);

  const handleBuscar = () => {
    const nextSearch = search.trim();
    setAppliedSearch(nextSearch);
    setPage(1);
    fetchRows({ usePage: 1, useSearch: nextSearch, useLimit: limit });
  };

  const handleSync = async (unit) => {
    const id = getUnidadId(unit);
    if (!id) return;

    try {
      setSyncing((s) => ({ ...s, [id]: true }));
      await syncUnit(id);
      setSnack({ open: true, type: "success", msg: "Unidad sincronizada con equipo_modem." });
      fetchRows();
    } catch (e) {
      setSnack({
        open: true,
        type: "error",
        msg: e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo sincronizar",
      });
    } finally {
      setSyncing((s) => ({ ...s, [id]: false }));
    }
  };

  const handleDelete = async (unit) => {
    const id = getUnidadId(unit);
    if (!id || !deleteUnit) return;

    const ok = window.confirm(`Desea eliminar la unidad ${getUnidadNombre(unit)} / UID ${getUnidadUid(unit)}?`);
    if (!ok) return;

    try {
      setDeleting((s) => ({ ...s, [id]: true }));
      await deleteUnit(id);
      setSnack({ open: true, type: "success", msg: "Unidad eliminada correctamente." });
      fetchRows();
    } catch (e) {
      setSnack({
        open: true,
        type: "error",
        msg: e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo eliminar la unidad",
      });
    } finally {
      setDeleting((s) => ({ ...s, [id]: false }));
    }
  };

  const handleCreateChange = (field) => (event) => {
    setCreateForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEditChange = (field) => (event) => {
    setEditForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const resetSelects = () => {
    setHwInput("");
    setHwOptions([]);
    setHwSelected(null);
    setGroupInput("");
    setGroupOptions([]);
    setGroupPage(1);
    setGroupTotalPages(1);
    setChipInput("");
    setChipOptions([]);
    setChipSelected(null);
  };

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setCreateForm(emptyUnitForm);
    resetSelects();
  };

  const openEdit = (row) => {
    const currentHwType = getUnidadHwType(row);
    setEditRow(row);
    setEditForm({
      nombre: getUnidadNombre(row) === "-" ? "" : getUnidadNombre(row),
      uid: getUnidadUid(row) === "-" ? "" : String(getUnidadUid(row)),
      hwTypeId: currentHwType?.id ? String(currentHwType.id) : "",
      sensorAlias: getUnidadSensorAlias(row),
      iconLibId: "",
      iconPath: "",
      grupoIds: getUnidadGrupos(row).map(getGrupoId).filter((id) => id !== undefined && id !== null).map(String),
      observacion: "",
    });
    resetSelects();
    setHwSelected(currentHwType);
    setHwInput(currentHwType ? getHwOptionLabel(currentHwType) : "");
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (updating) return;
    setEditOpen(false);
    setEditRow(null);
    setEditForm(emptyUnitForm);
    resetSelects();
  };

  const handleCreate = async () => {
    const uid = createForm.uid.trim();
    const hwTypeId = createForm.hwTypeId.trim();
    const chipNumero = createForm.chipNumero.trim();
    if (!uid || !hwTypeId || !chipNumero) {
      setSnack({
        open: true,
        type: "error",
        msg: !uid
          ? "Debe indicar el UID."
          : !hwTypeId
            ? "Debe seleccionar el tipo de dispositivo."
            : "Debe seleccionar obligatoriamente un chip.",
      });
      return;
    }

    try {
      setCreating(true);
      await createUnit({
        modem: uid,
        nombre: createForm.nombre.trim(),
        uid,
        hwTypeId: Number(hwTypeId),
        sensorAlias: createForm.sensorAlias.trim(),
        iconLibId: createForm.iconPath.trim() ? Number(createForm.iconLibId || 0) : undefined,
        iconPath: createForm.iconPath.trim() || undefined,
        chipNumero,
        grupoIds: createForm.grupoIds.map(Number).filter((id) => !Number.isNaN(id)),
        observacion: createForm.observacion.trim(),
      });
      setSnack({ open: true, type: "success", msg: "Vehiculo agregado y registrado en historial_modem." });
      setCreateOpen(false);
      setCreateForm(emptyUnitForm);
      resetSelects();
      fetchRows({ usePage: 1 });
      setPage(1);
    } catch (e) {
      setSnack({
        open: true,
        type: "error",
        msg: e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo agregar el vehiculo",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async () => {
    const id = getUnidadId(editRow);
    if (!id) {
      setSnack({ open: true, type: "error", msg: "No se encontro el ID de la unidad." });
      return;
    }

    const originalName = getUnidadNombre(editRow) === "-" ? "" : getUnidadNombre(editRow);
    const originalUid = getUnidadUid(editRow) === "-" ? "" : String(getUnidadUid(editRow));
    const payload = {
      observacion: editForm.observacion.trim(),
    };

    if (editForm.nombre.trim() !== originalName) payload.nombre = editForm.nombre.trim();
    if (editForm.uid.trim() !== originalUid || editForm.hwTypeId) payload.uid = editForm.uid.trim();
    if (editForm.hwTypeId) payload.hwTypeId = Number(editForm.hwTypeId);
    if (editForm.sensorAlias.trim()) payload.sensorAlias = editForm.sensorAlias.trim();
    if (editForm.iconPath.trim()) {
      payload.iconLibId = Number(editForm.iconLibId || 0);
      payload.iconPath = editForm.iconPath.trim();
    }
    payload.grupoIds = editForm.grupoIds.map(Number).filter((grupoId) => !Number.isNaN(grupoId));

    try {
      setUpdating(true);
      await updateUnit(id, payload);
      setSnack({ open: true, type: "success", msg: "Unidad actualizada correctamente." });
      setEditOpen(false);
      setEditRow(null);
      setEditForm(emptyUnitForm);
      resetSelects();
      fetchRows();
    } catch (e) {
      setSnack({
        open: true,
        type: "error",
        msg: e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo actualizar la unidad",
      });
    } finally {
      setUpdating(false);
    }
  };

  const headerStats = useMemo(() => {
    const matched = rows.filter((r) => r?.modem_local).length;
    const synced = rows.filter((r) => r?.sincronizado).length;
    return { matched, synced, total };
  }, [rows, total]);

  const statCards = [
    {
      title: "Total unidades",
      value: headerStats.total,
      helper: "Unidades registradas",
      icon: <DirectionsCarIcon />,
      color: "#0ea5a6",
      bg: "#dff7f5",
    },
    {
      title: "Con modem",
      value: headerStats.matched,
      helper: "Unidades con modem",
      icon: <SmartphoneIcon />,
      color: "#1e88e5",
      bg: "#e6f1ff",
    },
    {
      title: "Sincronizados",
      value: headerStats.synced,
      helper: "Unidades sincronizadas",
      icon: <SyncIcon />,
      color: "#2eb85c",
      bg: "#e9f7ef",
    },
    {
      title: "Ultima actualizacion",
      value: `Hoy ${formatTimeShort(lastUpdated)}`,
      helper: formatDateLong(lastUpdated),
      icon: <EventIcon />,
      color: "#7367f0",
      bg: "#eeeaff",
    },
  ];

  const pageStart = total > 0 ? ((page - 1) * limit) + 1 : 0;
  const pageEnd = Math.min(page * limit, total);
  const paginationItems = useMemo(() => {
    if (totalPages <= 1) return [];
    const set = new Set([1, page - 1, page, page + 1, totalPages]);
    return [...set]
      .filter((n) => n >= 1 && n <= totalPages)
      .sort((a, b) => a - b)
      .reduce((acc, n, index, arr) => {
        if (index > 0 && n - arr[index - 1] > 1) acc.push("ellipsis");
        acc.push(n);
        return acc;
      }, []);
  }, [page, totalPages]);
  const mapIcon = mapDialog?.row ? getUnidadIcon(mapDialog.row) : { url: "", path: "" };
  const mapIconUrl = resolveUnidadIconUrl(mapIcon, iconUrlMap);

  return (
    <DashboardLayout>
      <Box
        sx={{
          minHeight: "100vh",
          p: { xs: 1.25, md: 2 },
          bgcolor: "background.default",
          backgroundImage: "linear-gradient(135deg, rgba(14, 165, 166, 0.05), rgba(115, 103, 240, 0.04))",
        }}
      >
        <PageHeader title={title} subtitle={subtitle} backTo=".." sx={{ mb: 2, borderRadius: 1 }} />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
          {statCards.map((card) => (
            <Paper
              key={card.title}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #e5e7eb",
                bgcolor: translucentPaperBg,
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.07)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minHeight: 108,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: card.bg,
                  color: card.color,
                  boxShadow: `0 8px 22px ${card.color}33`,
                  "& svg": { fontSize: 29 },
                }}
              >
                {card.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", fontWeight: 700 }}>
                  {card.title}
                </Typography>
                <Typography sx={{ fontSize: { xs: "1.35rem", md: "1.55rem" }, lineHeight: 1.1, fontWeight: 900 }}>
                  {typeof card.value === "number" ? card.value.toLocaleString("es-BO") : card.value}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.25 }}>
                  {card.helper}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 0.8, md: 1 },
            mb: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            borderRadius: 1.5,
            border: "1px solid #e5e7eb",
            bgcolor: translucentPaperBg,
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.06)",
          }}
        >
          <TextField
            size="small"
            placeholder="Buscar unidad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleBuscar();
            }}
            InputProps={{ endAdornment: <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} /> }}
            sx={{
              width: { xs: "100%", md: 310 },
              "& .MuiInputBase-root": { borderRadius: 1.5, minHeight: 34 },
              "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 },
            }}
          />
          <TextField
            select
            size="small"
            label="Limite"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            sx={{
              width: 92,
              "& .MuiInputBase-root": { borderRadius: 1.5, minHeight: 34 },
              "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 },
              "& .MuiInputLabel-root": { fontSize: "0.74rem" },
            }}
          >
            {limitOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
          <LoadingButton
            loading={loading}
            variant="contained"
            disableElevation
            startIcon={<SearchIcon />}
            onClick={handleBuscar}
            sx={{ minHeight: 34, px: 1.8, borderRadius: 1.5, bgcolor: providerColor, "&:hover": { bgcolor: providerColor } }}
          >
            Buscar
          </LoadingButton>
          <Box sx={{ flex: 1 }} />
          <Button
            disabled={loading}
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={() => fetchRows()}
            sx={{ borderRadius: 1.5, minHeight: 34, bgcolor: "background.paper", px: 1.6 }}
          >
            Actualizar
          </Button>
          <Button
            disabled={loading || !createUnit}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: 1.5, minHeight: 34, px: 1.8, bgcolor: providerColor, "&:hover": { bgcolor: providerColor } }}
          >
            Agregar vehiculo
          </Button>
        </Paper>

        {!!err && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {err}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ opacity: loading ? 0.65 : 1 }}>
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                minWidth: 1100,
                "& .MuiTableCell-root": { fontSize: "0.78rem", py: 0.65, px: 0.8 },
              }}
            >
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 900, bgcolor: tableHeadBg } }}>
                  <TableCell align="center">Icono</TableCell>
                  <TableCell>Unidad</TableCell>
                  <TableCell>UID</TableCell>
                  <TableCell>Grupos</TableCell>
                  <TableCell>Modem local</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Estado SORT</TableCell>
                  <TableCell>Ciudad</TableCell>
                  <TableCell>Ultimo reporte</TableCell>
                  <TableCell>Coordenadas</TableCell>
                  <TableCell>Plataforma</TableCell>
                  <TableCell align="right">Accion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      No hay unidades para mostrar.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const id = getUnidadId(row);
                    const modem = row?.modem_local;
                    const isSyncing = !!syncing[id];
                    const isDeleting = !!deleting[id];
                    const position = getUnidadPosition(row);
                    const lastTime = getUnidadLastTime(row);
                    const detalleId = modem?.num_sim || modem?.emai || modem?.id;
                    const icon = getUnidadIcon(row);
                    const iconUrl = resolveUnidadIconUrl(icon, iconUrlMap);

                    return (
                      <TableRow key={id || `${row?.nm}-${row?.uid}`} hover>
                        <TableCell align="center" sx={{ width: 52 }}>
                          {iconUrl ? (
                            <Tooltip title={getUnidadNombre(row)}>
                              <Box
                                component="img"
                                src={iconUrl}
                                alt={getUnidadNombre(row)}
                                onClick={() => setMapDialog({ row, position })}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  objectFit: "contain",
                                  display: "inline-block",
                                  verticalAlign: "middle",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title={getUnidadNombre(row)}>
                              <Box
                                onClick={() => setMapDialog({ row, position })}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  mx: "auto",
                                  borderRadius: 1,
                                  display: "grid",
                                  placeItems: "center",
                                  bgcolor: (theme) => theme.palette.action.hover,
                                  color: "text.disabled",
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: (theme) => theme.palette.action.selected },
                                }}
                              >
                                <ImageIcon sx={{ fontSize: 17 }} />
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{getUnidadNombre(row)}</TableCell>
                        <TableCell>{getUnidadUid(row)}</TableCell>
                        <TableCell>
                          {getUnidadGrupos(row).length ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                              {getUnidadGrupos(row).map((grupo) => (
                                <Chip
                                  key={grupo.id}
                                  size="small"
                                  label={grupo.nm || grupo.name || grupo.id}
                                  sx={{ height: 19, fontSize: "0.66rem" }}
                                />
                              ))}
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {modem ? (
                            <Box>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() =>
                                  setEquipoDialog({
                                    tipo: "modem",
                                    identificador: detalleId,
                                  })
                                }
                                sx={{
                                  p: 0,
                                  minWidth: 0,
                                  fontSize: "0.78rem",
                                  fontWeight: 900,
                                  textTransform: "none",
                                  justifyContent: "flex-start",
                                }}
                              >
                                {modem.num_sim || modem.id || "-"}
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() =>
                                  setEquipoDialog({
                                    tipo: "modem",
                                    identificador: modem.emai || detalleId,
                                  })
                                }
                                sx={{
                                  p: 0,
                                  minWidth: 0,
                                  display: "block",
                                  fontSize: "0.7rem",
                                  color: "text.secondary",
                                  textTransform: "none",
                                  justifyContent: "flex-start",
                                }}
                              >
                                {modem.emai || "-"}
                              </Button>
                            </Box>
                          ) : (
                            <Chip size="small" label="No encontrado" />
                          )}
                        </TableCell>
                        <TableCell>{modem?.modelo || "-"}</TableCell>
                        <TableCell>{modem?.estado || "-"}</TableCell>
                        <TableCell>{modem?.ciudad || "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(lastTime)}</TableCell>
                        <TableCell>
                          {position ? (
                            <Button
                              size="small"
                              startIcon={<MapIcon />}
                              onClick={() => setMapDialog({ row, position })}
                              sx={{ textTransform: "none" }}
                            >
                              {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
                            </Button>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={row?.sincronizado ? "success" : "default"}
                            label={modem?.ip || "-"}
                            sx={{ fontSize: "0.68rem" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                            <Tooltip title="Editar unidad">
                              <span>
                                <IconButton size="small" disabled={!updateUnit} onClick={() => openEdit(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <LoadingButton
                              size="small"
                              loading={isSyncing}
                              disabled={!modem || row?.sincronizado}
                              startIcon={<SyncIcon />}
                              onClick={() => handleSync(row)}
                            >
                              Sincronizar
                            </LoadingButton>
                            <Tooltip title="Eliminar unidad">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={!deleteUnit || isDeleting}
                                  onClick={() => handleDelete(row)}
                                >
                                  {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {totalPages > 1 && (
          <Paper
            elevation={0}
            sx={{
              mt: 1,
              px: { xs: 1, md: 1.5 },
              py: 1,
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              bgcolor: translucentPaperBg,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", fontWeight: 700 }}>
              Mostrando {pageStart} a {pageEnd} de {total.toLocaleString("es-BO")} unidades
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              <IconButton
                size="small"
                disabled={page <= 1 || loading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                sx={{
                  width: 34,
                  height: 34,
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              {paginationItems.map((item, index) => (
                item === "ellipsis" ? (
                  <Typography key={`ellipsis-${index}`} sx={{ px: 0.5, color: "text.secondary", fontWeight: 900 }}>
                    ...
                  </Typography>
                ) : (
                  <Button
                    key={item}
                    size="small"
                    disabled={loading}
                    onClick={() => setPage(item)}
                    sx={{
                      minWidth: 34,
                      width: 34,
                      height: 34,
                      p: 0,
                      borderRadius: "50%",
                      fontWeight: 900,
                      color: item === page ? "#fff" : "text.primary",
                      bgcolor: item === page ? providerColor : "background.paper",
                      border: "1px solid #e2e8f0",
                      boxShadow: item === page ? `0 8px 18px ${providerColor}33` : "none",
                      "&:hover": {
                        bgcolor: item === page ? providerColor : "action.hover",
                      },
                    }}
                  >
                    {item}
                  </Button>
                )
              ))}
              <IconButton
                size="small"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                sx={{
                  width: 34,
                  height: 34,
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )}

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert
            severity={snack.type}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>

        <CentroEquipoDetalleDialog
          open={!!equipoDialog}
          tipo={equipoDialog?.tipo || "modem"}
          identificador={equipoDialog?.identificador}
          title="Detalle del modem"
          onClose={() => setEquipoDialog(null)}
        />

        <Dialog
          open={iconDialog.open}
          onClose={() => setIconDialog((prev) => ({ ...prev, open: false }))}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
            Biblioteca de iconos
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Iconos de unidades
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1 }}>
            {!!iconError && <Alert severity="error" sx={{ mb: 1 }}>{iconError}</Alert>}
            <Box sx={{ display: "flex", gap: 0.75, mb: 1, alignItems: "center" }}>
              <Button
                size="small"
                disabled={iconLoading}
                onClick={() => loadIconLibrary({ target: iconDialog.target })}
              >
                Actualizar
              </Button>
              {iconLoading && <CircularProgress size={18} />}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", md: "repeat(6, 1fr)" },
                gap: 0.75,
                minHeight: 180,
              }}
            >
              {iconItems.map((item) => (
                <Button
                  key={item.path || item.name}
                  variant="outlined"
                  onClick={() => selectIcon(item)}
                  sx={{
                    minHeight: 76,
                    p: 0.75,
                    textTransform: "none",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 0.5,
                    overflow: "hidden",
                  }}
                >
                  {item.isDir ? (
                    <FolderIcon color="warning" />
                  ) : item.imageUrl ? (
                    <Box component="img" src={item.imageUrl} alt={item.name} sx={{ width: 32, height: 32, objectFit: "contain" }} />
                  ) : (
                    <ImageIcon />
                  )}
                  <Typography variant="caption" sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.name}
                  </Typography>
                </Button>
              ))}
              {!iconLoading && iconItems.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ gridColumn: "1 / -1", p: 2 }}>
                  No hay iconos para mostrar.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 0.75 }}>
            <Button size="small" onClick={() => setIconDialog((prev) => ({ ...prev, open: false }))}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!mapDialog} onClose={() => setMapDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {mapIconUrl ? (
                <Box
                  component="img"
                  src={mapIconUrl}
                  alt={getUnidadNombre(mapDialog?.row)}
                  sx={{ width: 32, height: 32, objectFit: "contain", flex: "0 0 auto" }}
                />
              ) : (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: (theme) => theme.palette.action.hover,
                    color: "text.disabled",
                    flex: "0 0 auto",
                  }}
                >
                  <ImageIcon sx={{ fontSize: 18 }} />
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                Ubicacion de la unidad
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {getUnidadNombre(mapDialog?.row)} - {formatDateTime(getUnidadLastTime(mapDialog?.row))}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1 }}>
            {mapDialog?.position ? (
              <Box>
                <Box sx={{ mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip size="small" label={`Lat: ${mapDialog.position.lat}`} />
                  <Chip size="small" label={`Lng: ${mapDialog.position.lng}`} />
                  <Button
                    size="small"
                    target="_blank"
                    rel="noreferrer"
                    href={`https://www.google.com/maps?q=${mapDialog.position.lat},${mapDialog.position.lng}`}
                  >
                    Abrir en Google Maps
                  </Button>
                </Box>
                <Box
                  component="iframe"
                  title="Mapa"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapDialog.position.lng - 0.01}%2C${mapDialog.position.lat - 0.01}%2C${mapDialog.position.lng + 0.01}%2C${mapDialog.position.lat + 0.01}&layer=mapnik&marker=${mapDialog.position.lat}%2C${mapDialog.position.lng}`}
                  sx={{
                    width: "100%",
                    height: 420,
                    border: "1px solid #d1d5db",
                    borderRadius: 1,
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Esta unidad no tiene coordenadas disponibles.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 0.75 }}>
            <Button size="small" onClick={() => setMapDialog(null)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={createOpen} onClose={closeCreate} maxWidth="md" fullWidth>
          <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
            Agregar vehiculo
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              El UID se usara para buscar el modem en equipo_modem.
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
              <Autocomplete
                size="small"
                options={hwOptions}
                loading={hwLoading}
                value={hwSelected}
                inputValue={hwInput}
                isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                getOptionLabel={getHwOptionLabel}
                filterOptions={(options) => options}
                onInputChange={(_, value) => setHwInput(value)}
                onChange={(_, option) => {
                  setHwSelected(option || null);
                  setCreateForm((prev) => ({ ...prev, hwTypeId: option?.id ? String(option.id) : "" }));
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                        {option.name || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {option.id || "-"} {option.tp ? `- TP ${option.tp}` : ""} {option.up ? `- UP ${option.up}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo de dispositivo *"
                    helperText={createForm.hwTypeId ? `ID seleccionado: ${createForm.hwTypeId}` : "Busca por nombre, ID o TP"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {hwLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                size="small"
                label="Nombre de unidad"
                value={createForm.nombre}
                onChange={handleCreateChange("nombre")}
                helperText="Ingrese placa o nombre"
              />
              <TextField
                size="small"
                label="UID"
                value={createForm.uid}
                onChange={handleCreateChange("uid")}
                helperText="Ingrese EMAI o numero de serie"
              />
              <TextField
                size="small"
                label="Sensor alias"
                value={createForm.sensorAlias}
                onChange={handleCreateChange("sensorAlias")}
                helperText="Ejemplo: ALIAS:YPFB-0992"
              />
              <Autocomplete
                size="small"
                options={chipOptions}
                loading={chipLoading}
                value={chipSelected}
                inputValue={chipInput}
                isOptionEqualToValue={(option, value) => String(option?.numero) === String(value?.numero)}
                getOptionLabel={getChipOptionLabel}
                filterOptions={(options) => options}
                onInputChange={(_, value) => setChipInput(value)}
                onChange={(_, option) => {
                  setChipSelected(option || null);
                  setCreateForm((prev) => ({ ...prev, chipNumero: option?.numero ? String(option.numero) : "" }));
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.numero || option.ID}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                        {option.numero || "-"} {option.emai ? `- ${option.emai}` : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.modelo || "-"} | {option.ciudad || "-"} | {option.estado || "-"}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Chip relacionado *"
                    required
                    helperText="Obligatorio. Solo chips Inactivos de tu sucursal"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {chipLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                disabled={!listIconLibrary}
                onClick={() => openIconLibrary("create")}
                sx={{ minHeight: 40, justifyContent: "flex-start" }}
              >
                Abrir biblioteca de iconos
              </Button>
              {listUnitGroups && (
                <GroupCheckList
                  rows={groupOptions}
                  selectedIds={createForm.grupoIds}
                  onChange={(ids) => setCreateForm((prev) => ({ ...prev, grupoIds: ids }))}
                  loading={groupLoading}
                  filter={groupInput}
                  onFilterChange={setGroupInput}
                  page={groupPage}
                  totalPages={groupTotalPages}
                  onPageChange={(nextPage) => loadGroups({ usePage: nextPage, useSearch: groupInput })}
                />
              )}
              <TextField
                size="small"
                label="Observacion"
                value={createForm.observacion}
                onChange={handleCreateChange("observacion")}
                multiline
                minRows={2}
                sx={{ gridColumn: { xs: "auto", md: "1 / span 2" } }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 0.75 }}>
            <Button size="small" onClick={closeCreate} disabled={creating}>
              Cancelar
            </Button>
            <LoadingButton
              size="small"
              loading={creating}
              disabled={hwLoading || groupLoading}
              variant="contained"
              onClick={handleCreate}
            >
              Guardar
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog open={editOpen} onClose={closeEdit} maxWidth="md" fullWidth>
          <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
            Editar unidad
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              ID {getUnidadId(editRow) || "-"} - {getUnidadNombre(editRow)}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
              <TextField
                size="small"
                label="Nombre de unidad"
                value={editForm.nombre}
                onChange={handleEditChange("nombre")}
              />
              <TextField
                size="small"
                label="UID"
                value={editForm.uid}
                onChange={handleEditChange("uid")}
              />
              <TextField
                size="small"
                label="Sensor alias"
                value={editForm.sensorAlias}
                onChange={handleEditChange("sensorAlias")}
                helperText="Ejemplo: ALIAS:YPFB-0992"
              />
              <TextField
                size="small"
                label="Biblioteca icono"
                value={editForm.iconLibId}
                onChange={handleEditChange("iconLibId")}
                helperText="libId, usa 0 para biblioteca base"
              />
              <TextField
                size="small"
                label="Ruta icono"
                value={editForm.iconPath}
                onChange={handleEditChange("iconPath")}
                helperText="Ruta del icono en la biblioteca"
              />
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                disabled={!listIconLibrary}
                onClick={() => openIconLibrary("edit")}
                sx={{ minHeight: 40, justifyContent: "flex-start" }}
              >
                Abrir biblioteca de iconos
              </Button>
              <Autocomplete
                size="small"
                options={hwOptions}
                loading={hwLoading}
                value={hwSelected}
                inputValue={hwInput}
                isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                getOptionLabel={getHwOptionLabel}
                filterOptions={(options) => options}
                onInputChange={(_, value) => setHwInput(value)}
                onChange={(_, option) => {
                  setHwSelected(option || null);
                  setEditForm((prev) => ({ ...prev, hwTypeId: option?.id ? String(option.id) : "" }));
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                        {option.name || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {option.id || "-"} {option.tp ? `- TP ${option.tp}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo de dispositivo"
                    helperText={editForm.hwTypeId ? `Nuevo ID: ${editForm.hwTypeId}` : "Opcional, solo si deseas cambiarlo"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {hwLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {listUnitGroups && (
                <GroupCheckList
                  rows={mergeById(getUnidadGrupos(editRow), groupOptions, getGrupoId)}
                  selectedIds={editForm.grupoIds}
                  onChange={(ids) => setEditForm((prev) => ({ ...prev, grupoIds: ids }))}
                  loading={groupLoading}
                  filter={groupInput}
                  onFilterChange={setGroupInput}
                  page={groupPage}
                  totalPages={groupTotalPages}
                  onPageChange={(nextPage) => loadGroups({ usePage: nextPage, useSearch: groupInput })}
                />
              )}
              <TextField
                size="small"
                label="Observacion"
                value={editForm.observacion}
                onChange={handleEditChange("observacion")}
                multiline
                minRows={2}
                sx={{ gridColumn: { xs: "auto", md: "1 / span 2" } }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 0.75 }}>
            <Button size="small" onClick={closeEdit} disabled={updating}>
              Cancelar
            </Button>
            <LoadingButton
              size="small"
              loading={updating}
              disabled={hwLoading || groupLoading}
              variant="contained"
              onClick={handleUpdate}
            >
              Guardar cambios
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}
